import React, { useState } from "react"
import moment from "moment"
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap"
import * as backend from "../../helpers/backend_helper"
import Toaster from "./Toaster"

// Shared building blocks for the Report Validation (admin review) workflow.
//
//  - <ValidationBadge>          a small "Validated / Rejected" chip. Visible to
//                               everyone, on report pages, list rows and PDFs.
//  - <ReportValidationControls> admin-only Validate / Reject buttons (+ a remarks
//                               modal for rejection, and optional per-item ✓/✗).

export type Validation = {
  status?: "validated" | "rejected"
  remarks?: string
  by?: { id?: string; name?: string }
  at?: string
  items?: { [id: string]: "yes" | "no" | undefined }
} | null

const COLORS = {
  validated: { fg: "#1c8577", bg: "#e5f6f2", bd: "#2ea99b", icon: "✓", label: "Validated" },
  rejected: { fg: "#c0392b", bg: "#fdecec", bd: "#e07a6f", icon: "✗", label: "Rejected" },
}

// Compact chip. `size="sm"` for list/date columns, default for report headers.
// `iconOnly` renders just the ✓/✗ in a coloured circle — for tight spots such as
// a daily-monitoring day-column header where a full "✓ Validated" chip won't fit.
export const ValidationBadge = ({ validation, size, iconOnly }: { validation: Validation; size?: "sm"; iconOnly?: boolean }) => {
  if (!validation || !validation.status) return null
  const c = COLORS[validation.status]
  if (!c) return null
  const sm = size === "sm"
  if (iconOnly) {
    return (
      <span
        title={validation.remarks ? `${c.label}: ${validation.remarks}` : c.label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 15,
          height: 15,
          borderRadius: "50%",
          fontWeight: 800,
          fontSize: 10,
          lineHeight: 1,
          color: "#fff",
          backgroundColor: c.bd,
          WebkitPrintColorAdjust: "exact",
        }}
      >
        {c.icon}
      </span>
    )
  }
  return (
    <span
      title={validation.remarks ? `${c.label}: ${validation.remarks}` : c.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: sm ? 3 : 5,
        padding: sm ? "1px 7px" : "3px 10px",
        borderRadius: 12,
        fontWeight: 700,
        fontSize: sm ? 11 : 13,
        color: c.fg,
        backgroundColor: c.bg,
        border: `1px solid ${c.bd}`,
        WebkitPrintColorAdjust: "exact",
      }}
    >
      <span style={{ fontWeight: 800 }}>{c.icon}</span> {c.label}
    </span>
  )
}

interface ItemDef { id: string; name: string }

interface Props {
  reportType: string
  dateKey: any
  orgId: string
  facId: string
  validation: Validation
  isAdmin: boolean
  submitted: boolean            // a report exists to review
  onChange?: (v: Validation) => void
  by?: { id?: string; name?: string }
  items?: ItemDef[]             // optional: per-document / per-task ✓/✗ validation (rendered inside)
  externalItems?: { [id: string]: "yes" | "no" | undefined }  // optional: per-item ✓/✗ managed by the parent (rendered inline near each entry)
  submitedBy?: any              // optional: narrows lookup for per-user reports (daily work)
}

const ReportValidationControls = (props: Props) => {
  const { reportType, dateKey, orgId, facId, validation, isAdmin, submitted, onChange, by, items, externalItems, submitedBy } = props
  const [rejectOpen, setRejectOpen] = useState(false)
  const [remarks, setRemarks] = useState("")
  const [busy, setBusy] = useState(false)
  // Per-item ✓/✗ state (only used when `items` is supplied).
  const [itemState, setItemState] = useState<{ [id: string]: "yes" | "no" | undefined }>(
    (validation && validation.items) || {}
  )

  const save = async (status: "validated" | "rejected" | null, rmk?: string) => {
    setBusy(true)
    try {
      // Per-item ✓/✗ come from the parent (externalItems, rendered inline near
      // each entry) or from the internal toggles (items list).
      const usedItems = externalItems || itemState
      const body: any = { status, by }
      if (rmk != null) body.remarks = rmk
      if (externalItems || (items && items.length)) body.items = usedItems
      if (submitedBy) body.submitedBy = submitedBy
      await backend.setReportValidation(orgId, facId, reportType, dateKey, body)
      const next: Validation = status
        ? { status, remarks: rmk || "", by, at: new Date().toISOString(), items: usedItems }
        : null
      onChange && onChange(next)
      Toaster.success(status === "rejected" ? "Report rejected" : status ? "Report validated" : "Validation cleared")
      setRejectOpen(false)
      setRemarks("")
    } catch (e: any) {
      Toaster.error(typeof e === "string" ? e : "Could not update validation")
    } finally {
      setBusy(false)
    }
  }

  if (!submitted) return null

  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
      <ValidationBadge validation={validation} />
      {validation && validation.status && validation.by && validation.by.name && (
        <span style={{ fontSize: 12, color: "#6b7785" }}>
          {validation.status === "rejected" ? "Rejected" : "Validated"} by {validation.by.name}
          {validation.at ? ` on ${moment(validation.at).format("DD MMM YYYY, hh:mm A")}` : ""}
        </span>
      )}
      {validation && validation.status === "rejected" && validation.remarks && (
        <span style={{ color: "#c0392b", fontSize: 13 }}>Reason: {validation.remarks}</span>
      )}

      {isAdmin && !(validation && validation.status) && (
        <>
          {/* Optional per-document / per-task ✓ / ✗ toggles (before a decision) */}
          {items && items.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, width: "100%", margin: "6px 0" }}>
              {items.map((it) => (
                <div
                  key={it.id}
                  style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #e5e9ef", borderRadius: 6, padding: "3px 8px" }}
                >
                  <span style={{ fontSize: 13 }}>{it.name}</span>
                  <button
                    type="button"
                    title="Correct"
                    onClick={() => setItemState((s) => ({ ...s, [it.id]: "yes" }))}
                    style={{ border: "none", borderRadius: 4, cursor: "pointer", padding: "1px 7px", fontWeight: 800,
                      background: itemState[it.id] === "yes" ? "#2ea99b" : "#eef1f4", color: itemState[it.id] === "yes" ? "#fff" : "#556" }}
                  >✓</button>
                  <button
                    type="button"
                    title="Issue"
                    onClick={() => setItemState((s) => ({ ...s, [it.id]: "no" }))}
                    style={{ border: "none", borderRadius: 4, cursor: "pointer", padding: "1px 7px", fontWeight: 800,
                      background: itemState[it.id] === "no" ? "#e07a6f" : "#eef1f4", color: itemState[it.id] === "no" ? "#fff" : "#556" }}
                  >✗</button>
                </div>
              ))}
            </div>
          )}

          <Button color="success" size="sm" disabled={busy} onClick={() => save("validated")}>
            <i className="mdi mdi-check-circle-outline" /> Validate
          </Button>
          <Button color="danger" size="sm" disabled={busy} onClick={() => { setRemarks(""); setRejectOpen(true) }}>
            <i className="mdi mdi-close-circle-outline" /> Reject
          </Button>
        </>
      )}

      {/* Once validated/rejected the decision buttons are hidden; an admin can
          re-open the review with Clear. */}
      {isAdmin && validation && validation.status && (
        <Button color="light" size="sm" disabled={busy} onClick={() => save(null)}>
          Clear
        </Button>
      )}

      <Modal isOpen={rejectOpen} toggle={() => setRejectOpen(false)}>
        <ModalHeader toggle={() => setRejectOpen(false)}>Reject report</ModalHeader>
        <ModalBody>
          <label style={{ fontWeight: 600, marginBottom: 6 }}>Reason for rejection (required)</label>
          <Input
            type="textarea"
            rows={4}
            value={remarks}
            placeholder="Describe what needs to be corrected…"
            onChange={(e) => setRemarks(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button color="danger" disabled={busy || !remarks.trim()} onClick={() => save("rejected", remarks.trim())}>
            Reject
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ReportValidationControls
